import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box, TextField, Typography, Paper, CircularProgress, Alert,
    Stack, Tabs, Tab, Table, TableBody, TableRow, TableCell
} from '@mui/material';
import * as api from './countriesApi';
import type { Country, Vat } from './countriesApi';
import { sx14, paperSx, FormRow, DetailHeader, mergeByKey } from '../common/detailView';

interface Props {
    countryId: number | null;
}

export function CountryView({ countryId }: Props) {
    const { t } = useTranslation();

    const [item, setItem] = useState<Country | null>(null);
    const [vats, setVats] = useState<Vat[]>([]);
    const [isEditing, setIsEditing] = useState(false);
    const [isNew, setIsNew] = useState(false);
    const [copy, setCopy] = useState<Country | null>(null);
    const [tab, setTab] = useState(0);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const mergeVats = (existing: Country['vats'], all: Vat[]) =>
        mergeByKey(existing, all, cv => cv.vat.id, vat => ({ vat, vatRate: vat.vatRate }));

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const vatsData = await api.getVats();
            setVats(vatsData);
            if (countryId === null) {
                setItem({ id: 0, code: '', name: '', vats: mergeVats([], vatsData) });
                setIsNew(true);
                setIsEditing(true);
            } else {
                const data = await api.getCountry(countryId);
                data.vats = mergeVats(data.vats ?? [], vatsData);
                setItem(data);
                setIsNew(false);
                setIsEditing(false);
            }
        } catch (err) {
            setError(String(err));
        } finally {
            setLoading(false);
        }
    }, [countryId]);

    useEffect(() => { load(); }, [load]);

    if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
    if (error && !item) return <Alert severity="error">{error}</Alert>;
    if (!item) return null;

    const startEdit = () => { setCopy(JSON.parse(JSON.stringify(item))); setIsEditing(true); };
    const cancelEdit = () => { if (copy) setItem(copy); setIsEditing(false); };

    const save = async () => {
        setSaving(true); setError(null);
        try {
            const response = isNew ? await api.createCountry(item) : await api.updateCountry(item);
            response.vats = mergeVats(response.vats ?? [], vats);
            setItem(response);
            setIsNew(false);
            setIsEditing(false);
        } catch (err) {
            setError(String(err));
        } finally {
            setSaving(false);
        }
    };

    const del = async () => {
        if (!window.confirm(t('country.confirmDelete'))) return;
        setSaving(true); setError(null);
        try {
            await api.deleteCountry(item.id);
            window.history.back();
        } catch (err) {
            setError(String(err));
        } finally {
            setSaving(false);
        }
    };

    const update = <K extends keyof Country>(field: K, value: Country[K]) => {
        setItem({ ...item, [field]: value });
    };

    const updateVatRate = (vatId: number, vatRate: number) => {
        update('vats', item.vats.map(cv =>
            cv.vat.id === vatId ? { ...cv, vatRate } : cv
        ));
    };

    const sortedVats = [...item.vats].sort((a, b) =>
        (a.vat.vatCode ?? '').localeCompare(b.vat.vatCode ?? '')
    );

    return (
        <Paper variant="outlined" sx={paperSx}>
            <DetailHeader
                title={`${item.code} ${item.name}`}
                isEditing={isEditing}
                isNew={isNew}
                saving={saving}
                canSave
                onEdit={startEdit}
                onSave={save}
                onCancel={cancelEdit}
                onDelete={del}
            />

            <Box p={2}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}

                <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
                    <Tab sx={sx14} label={t('role.country')} />
                    <Tab sx={sx14} label={t('country.vat')} />
                </Tabs>

                {tab === 0 && (
                    <Stack spacing={2}>
                        <FormRow label={t('country.name')}>
                            {!isEditing ? (
                                <Typography sx={sx14}>{item.name}</Typography>
                            ) : (
                                <TextField size="small" fullWidth inputProps={{ maxLength: 100 }}
                                    value={item.name} onChange={e => update('name', e.target.value)} />
                            )}
                        </FormRow>

                        <FormRow label={t('country.code')}>
                            {!isNew && !isEditing ? (
                                <Typography sx={sx14}>{item.code}</Typography>
                            ) : (
                                <TextField size="small" fullWidth
                                    inputProps={{ maxLength: 3, style: { textTransform: 'uppercase' } }}
                                    value={item.code}
                                    onChange={e => update('code', e.target.value.toUpperCase())} />
                            )}
                        </FormRow>
                    </Stack>
                )}

                {tab === 1 && (
                    <Table size="small">
                        <TableBody>
                            {sortedVats.map(cv => (
                                <TableRow key={cv.vat.id}>
                                    <TableCell sx={{ ...sx14, width: 300 }}>
                                        {cv.vat.vatCode} {cv.vat.vatName}
                                    </TableCell>
                                    <TableCell>
                                        {!isEditing ? (
                                            <Typography sx={sx14}>{cv.vatRate}</Typography>
                                        ) : (
                                            <TextField size="small" type="number" fullWidth
                                                value={cv.vatRate ?? 0}
                                                onChange={e => updateVatRate(cv.vat.id, e.target.value === '' ? 0 : Number(e.target.value))} />
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </Box>

            {saving && <Box display="flex" justifyContent="center" p={1}><CircularProgress size={20} /></Box>}
        </Paper>
    );
}
